import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import en from "locales/en.json";
import bn from "locales/bn.json";

type LanguageCode = "en" | "bn";

type TranslationDict = Record<string, unknown>;

type LanguageOption = { code: LanguageCode; label: string; badge: string };

type I18nContextValue = {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  languages: LanguageOption[];
};

const translations: Record<LanguageCode, TranslationDict> = {
  en,
  bn,
};

const LANG_KEY = "water-atm-language";

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const languageOptions: LanguageOption[] = [
  { code: "en", label: "English", badge: "EN" },
  { code: "bn", label: "বাংলা", badge: "BN" },
];

const getNestedValue = (dict: TranslationDict, key: string): string | undefined =>
  key.split(".").reduce<unknown>((acc, part) => {
    if (acc && typeof acc === "object" && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, dict) as string | undefined;

const applyParams = (
  value: string,
  params?: Record<string, string | number>
): string => {
  if (!params) {
    return value;
  }

  return value.replace(/\{\{(\w+)\}\}/g, (match, token) => {
    const replacement = params[token];
    return replacement !== undefined ? String(replacement) : match;
  });
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const stored = localStorage.getItem(LANG_KEY) as LanguageCode | null;
    return stored ?? "en";
  });

  const setLanguage = (next: LanguageCode) => {
    localStorage.setItem(LANG_KEY, next);
    setLanguageState(next);
  };

  const t = useCallback((key: string, params?: Record<string, string | number>) => {
    const value = getNestedValue(translations[language], key);
    if (value && typeof value === "string") {
      return applyParams(value, params);
    }
    const fallback = getNestedValue(translations.en, key);
    return typeof fallback === "string" ? applyParams(fallback, params) : key;
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t,
      languages: languageOptions,
    }),
    [language, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}
